import { Link, Outlet } from 'react-router-dom';
import { Headset, Loader2, ShieldAlert } from 'lucide-react';

import { BrixLogo } from '@/components/brand/BrixLogo';
import { useDeskAccess } from '@/hooks/usePhoneDesk';
import { signOut } from '@/lib/auth';

/**
 * DeskLayout — chrome for the phone desk (/desk).
 *
 * Deliberately NOT the customer AppShell and NOT /admin: a receptionist holds
 * no customer membership and is not an admin, so they get a navy bar, the
 * page, and a sign-out — no customer nav, no admin nav, no Mr. Bubbles.
 *
 * ⚠ This gate is presentation only. Every desk-* function and submit-order
 *   check the flag again server-side (authedPhoneDesk / isDeskOrder).
 */
export default function DeskLayout() {
  const { access, loading } = useDeskAccess();

  return (
    <div className="min-h-screen bg-brix-surface-alt">
      <header className="sticky top-0 z-30 bg-brix-primary text-white shadow-card">
        <div className="mx-auto flex h-14 max-w-[1400px] items-center justify-between gap-3 px-4">
          <Link to="/desk" className="flex min-w-0 items-center gap-3">
            <span className="flex shrink-0 items-center rounded-md bg-white px-2 py-1">
              <BrixLogo variant="full" size="sm" showTagline={false} label="Brix Beverage" />
            </span>
            <div className="min-w-0 leading-tight">
              <div className="flex items-center gap-1.5 truncate font-display text-sm font-bold tracking-display-tight">
                <Headset size={14} className="shrink-0" aria-hidden />
                Phone desk
              </div>
              <div className="truncate text-[11px] text-white/70">Brix Beverage customer line</div>
            </div>
          </Link>
          <div className="flex min-w-0 items-center gap-2">
            {access?.name && (
              <span className="hidden truncate text-xs text-white/80 sm:inline">{access.name}</span>
            )}
            {access?.isSuperadmin && (
              <Link
                to="/admin/quick-order"
                className="rounded-md px-2 py-1 text-xs text-white/80 transition-colors hover:bg-white/10 hover:text-white"
              >
                Admin
              </Link>
            )}
            <button
              type="button"
              onClick={() => void signOut()}
              className="rounded-md px-2 py-1 text-xs text-white/80 transition-colors hover:bg-white/10 hover:text-white"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] px-4 pb-16 pt-4">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-24 text-brix-muted">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span className="text-sm">Checking access…</span>
          </div>
        ) : !access?.allowed ? (
          <div className="mx-auto mt-16 max-w-sm rounded-xl border border-brix-border bg-brix-surface p-6 text-center shadow-card">
            <ShieldAlert className="mx-auto h-8 w-8 text-brix-warning" />
            <h1 className="mt-3 font-display text-lg font-bold text-brix-ink">Phone desk access required</h1>
            <p className="mt-2 text-sm text-brix-muted">
              This page is for the Brix Beverage phone desk. If you should have access, ask the
              office to turn on Phone desk for your login.
            </p>
            <Link to="/home" className="mt-4 inline-block text-sm font-semibold text-brix-primary underline">
              Back to the portal
            </Link>
          </div>
        ) : (
          <Outlet />
        )}
      </main>
    </div>
  );
}
